import React from 'react';
import { Button } from './Button';

export interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  className?: string;
}

/**
 * EmptyState Component v2 - FacturXPro Design System
 *
 * État vide avec icon, titre, description et CTA optionnel
 * Utilisé quand une liste/table est vide
 *
 * @example
 * ```tsx
 * <EmptyState
 *   icon={<FileTextIcon className="w-12 h-12" />}
 *   title="Aucune facture"
 *   description="Créez votre première facture pour commencer"
 *   ctaLabel="Nouvelle facture"
 *   onCtaClick={() => navigate('/invoices/new')}
 * />
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Empty State"
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  ctaLabel,
  onCtaClick,
  className = '',
}) => {
  return (
    <div
      className={`
        flex
        flex-col
        items-center
        justify-center
        py-16
        px-4
        text-center
        ${className}
      `.trim()}
    >
      <div className="mb-6 text-gray-400">{icon}</div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-base text-gray-500 mb-8 max-w-md">{description}</p>
      {ctaLabel && onCtaClick && (
        <Button variant="secondary" onClick={onCtaClick}>
          {ctaLabel}
        </Button>
      )}
    </div>
  );
};

export default EmptyState;
